import { Check } from "lucide-react";
import { Link } from "react-router-dom";

// [CONFIRM] Validate dollar ranges and engagement durations against current pricing
// before publishing externally. Ranges are deliberately conservative and shaped
// to qualify leads, not quote them.
const shapes = [
  {
    label: "Project",
    name: "Scoped Engagement",
    outcome:
      "Close a specific AI, application, privacy, or compliance risk. Most teams start with an AI Feature Security Review and walk away with a deliverable their engineering, security, legal, auditor, or enterprise buyer can act on.",
    duration: "10 business days to 6 weeks",
    range: "scoped fixed fee",
    blurb: "",
    includes: [
      "AI Feature Security Review as the most common starting point",
      "AI, application, API, and agentic workflow review",
      "Findings mapped to engineering and regulatory context",
      "Prioritized remediation and validation roadmap",
      "Deliverables suitable for audit, customer, and carrier review",
    ],
    primary: true,
  },
  {
    label: "Retainer",
    name: "Continuous Security & Operational Assurance",
    outcome:
      "Your AI environment changes every quarter. This retainer makes sure your security posture changes with it, continuously validated and documented so the next inquiry is answered before it becomes a problem.",
    duration: "Quarterly · annual minimum",
    range: "$8k–$25k / month",
    blurb: "",
    includes: [
      "Monthly posture report and quarterly controls re-assessment",
      "Regulatory change monitoring and advisory",
      "Named engagement lead with priority response",
    ],
    primary: false,
  },
  {
    label: "Advisory",
    name: "Fractional Security & Privacy Advisor",
    outcome:
      "The security and privacy judgment your organization needs, embedded across engineering, legal, and the executive team, without the cost of a full-time hire. A documented security function on the org chart, and a trusted advisor on call.",
    duration: "Monthly · rolling",
    range: "$4k–$10k / month",
    blurb: "",
    includes: [
      "Standing weekly office hours",
      "Customer security review and vendor / BAA support",
      "Audit and certification preparation",
    ],
    primary: false,
  },
];

export default function ArqEngagementShapes() {
  return (
    <section id="engagements" className="bg-arq-surface1/40 border-y border-arq-border py-20 md:py-[120px] px-6">
      <div className="max-w-[1200px] mx-auto">
        <div className="text-center max-w-[820px] mx-auto mb-14">
          <div className="arq-label mb-6">How We Engage</div>
          <h2
            className="font-display font-semibold text-arq-text mb-5"
            style={{ fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
          >
            Most teams start with an AI Feature Security Review.
          </h2>
          <p className="text-arq-muted text-[17px] leading-[1.75]">
            Clients begin with a scoped project, then transition into a continuous program or
            fractional advisory.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-5 md:gap-6">
          {shapes.map((s) => (
            <div
              key={s.name}
              className={`arq-card p-7 md:p-8 flex flex-col h-full ${
                s.primary ? "border-arq-blue/60 ring-1 ring-arq-blue/30" : ""
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono-tech text-[11px] tracking-[0.12em] uppercase text-arq-blue">
                  {s.label}
                </div>
                {s.primary && (
                  <span className="font-mono text-[10px] tracking-wider uppercase bg-arq-blue/10 border border-arq-blue/30 text-arq-blue rounded-full px-2.5 py-0.5">
                    Most clients
                  </span>
                )}
              </div>
              <h3 className="font-display text-[20px] font-semibold text-arq-text mb-3 leading-tight">
                {s.name}
              </h3>
              <p className="text-arq-text/90 text-[15px] leading-[1.6] mb-3">
                {s.outcome}
              </p>
              <div className="font-mono text-[12px] text-arq-tertiary mb-4">
                {s.duration} · <span className="text-arq-muted">{s.range}</span>
              </div>
              {s.blurb && <p className="text-arq-muted text-[14px] leading-[1.7] mb-5">{s.blurb}</p>}
              <ul className="space-y-2.5 mb-6 flex-1">
                {s.includes.map((it) => (
                  <li
                    key={it}
                    className="flex items-start gap-2.5 text-[14px] text-arq-text/90 leading-snug"
                  >
                    <Check
                      className="w-4 h-4 mt-0.5 flex-shrink-0"
                      style={{ color: "hsl(var(--arq-teal))" }}
                      aria-hidden
                    />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
              {s.primary ? (
                <Link
                  to="/security-intake"
                  className="mt-auto inline-flex items-center justify-center font-semibold text-[14px] px-5 py-3 rounded-md transition-colors bg-arq-blue text-white hover:bg-arq-blue/90"
                >
                  Start the intake
                </Link>
              ) : (
                <a
                  href="https://calendly.com/hello-arqeraai/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-auto inline-flex items-center justify-center font-semibold text-[14px] px-5 py-3 rounded-md transition-colors border border-arq-border text-arq-text hover:border-arq-blue hover:text-arq-blue"
                >
                  Explore this engagement
                </a>
              )}
            </div>
          ))}
        </div>

        <p className="text-arq-tertiary text-[12px] text-center mt-8 max-w-[820px] mx-auto leading-relaxed">
          Ranges are indicative and shaped to qualify scope. Final pricing is fixed in writing
          after a discovery call and never billed against an open hourly clock.
        </p>
      </div>
    </section>
  );
}
